import { ExperimentAnalysisRun } from "@/http/api-types";
import { isExperimentAnalysisResultArtifact } from "./analysis-result";

const defaultPrefectUIBaseURL = "http://prefect.service.dw2.consul:4200";

const dayInMilliseconds = 24 * 60 * 60 * 1000;

// The analysis query filters on `date(ts) > start AND date(ts) < end AND date(ts) < current_date()`, so the
// start day, the end day and today are all excluded. The first day it can cover is start+1 and the last is
// min(end, today)-1 — when that window is empty every CTE returns 0 rows and the flow fails while generating
// the data. Everything is compared in UTC because that is what BigQuery's current_date() uses.
export function getExperimentAnalysisDataWindowReason(
  startDate: string | undefined,
  endDate: string | undefined,
  now: Date = new Date(),
): string | undefined {
  const startDay = toUTCDayIndex(startDate);
  const endDay = toUTCDayIndex(endDate);
  const today = toUTCDayIndex(now);
  if (startDay === null || endDay === null || today === null) {
    return undefined;
  }

  const firstCoveredDay = startDay + 1;
  const lastCoveredDay = Math.min(endDay, today) - 1;
  if (firstCoveredDay <= lastCoveredDay) {
    return undefined;
  }

  if (endDay - startDay < 2) {
    return (
      "Analysis needs at least one full day between the start and end dates — the start and end days " +
      "themselves are never included."
    );
  }

  return (
    `Analysis needs one full day of data. The first day it can cover is ${formatUTCDayIndex(firstCoveredDay)}, ` +
    `so it can be scheduled from ${formatUTCDayIndex(firstCoveredDay + 1)} (UTC) onwards.`
  );
}

function toUTCDayIndex(value: string | Date | undefined): number | null {
  if (!value) {
    return null;
  }

  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return Math.floor(Date.UTC(parsed.getUTCFullYear(), parsed.getUTCMonth(), parsed.getUTCDate()) / dayInMilliseconds);
}

function formatUTCDayIndex(dayIndex: number): string {
  return new Date(dayIndex * dayInMilliseconds).toISOString().slice(0, 10);
}

export function mergeExperimentAnalysisRuns(
  selectedRun: ExperimentAnalysisRun | undefined,
  persistedRuns: ExperimentAnalysisRun[],
): ExperimentAnalysisRun[] {
  const runs = persistedRuns.map(run =>
    selectedRun?.flow_run_id === run.flow_run_id ? { ...run, ...selectedRun } : run,
  );

  if (!selectedRun || runs.some(run => run.flow_run_id === selectedRun.flow_run_id)) {
    return runs;
  }

  return [selectedRun, ...runs];
}

export function getActiveExperimentAnalysisRun(runs: ExperimentAnalysisRun[]): ExperimentAnalysisRun | undefined {
  return runs.find(run => !run.terminal);
}

export function getLatestExperimentAnalysisRunWithArtifact(
  runs: ExperimentAnalysisRun[],
): ExperimentAnalysisRun | undefined {
  return runs.find(run => isExperimentAnalysisResultArtifact(run.result_artifact));
}

export function formatRunDuration(totalRunTime?: number): string | null {
  if (typeof totalRunTime !== "number" || !Number.isFinite(totalRunTime) || totalRunTime < 0) {
    return null;
  }

  const roundedSeconds = Math.round(totalRunTime);
  const hours = Math.floor(roundedSeconds / 3600);
  const minutes = Math.floor((roundedSeconds % 3600) / 60);
  const seconds = roundedSeconds % 60;

  if (hours > 0) {
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  }

  if (minutes > 0) {
    return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
  }

  return `${seconds}s`;
}

export function getRunResourceHref(resource?: string): string | null {
  if (!resource) {
    return null;
  }

  const trimmedResource = resource.trim();
  if (!trimmedResource) {
    return null;
  }

  if (trimmedResource.startsWith("http://") || trimmedResource.startsWith("https://")) {
    return trimmedResource;
  }

  let objectPath = trimmedResource;
  if (objectPath.startsWith("gs://")) {
    objectPath = objectPath.slice("gs://".length);
  } else if (objectPath.includes("://")) {
    return null;
  } else if (!objectPath.includes("/experiment/")) {
    return null;
  }

  objectPath = objectPath.replace(/^\/+/, "");
  if (!objectPath || !objectPath.includes("/")) {
    return null;
  }

  return `https://storage.cloud.google.com/${objectPath}`;
}

export function getPrefectRunHref(flowRunID?: string, prefectUIURL?: string): string | null {
  const trimmedFlowRunID = flowRunID?.trim();
  if (!trimmedFlowRunID) {
    return null;
  }

  const baseURL = getPrefectUIBaseURL(prefectUIURL) ?? defaultPrefectUIBaseURL;
  return `${baseURL}/runs/flow-run/${encodeURIComponent(trimmedFlowRunID)}`;
}

function getPrefectUIBaseURL(prefectUIURL?: string): string | null {
  if (!prefectUIURL) {
    return null;
  }

  try {
    const parsedURL = new URL(prefectUIURL);
    const normalizedPath = parsedURL.pathname
      .replace(/\/runs\/flow-run\/.*$/, "")
      .replace(/\/flow-runs\/flow-run\/.*$/, "")
      .replace(/\/$/, "");

    return `${parsedURL.origin}${normalizedPath}`;
  } catch {
    return null;
  }
}
